import { useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { api, request, ApiError, type Schemas } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { useAnalysisContext } from '../../../analysis/context';
import { clampToSlice, useSliceDate } from '../../../analysis/useSliceDate';
import { defineMessages } from '../../../i18n/define';
import { COMMON } from '../../../i18n/common';
import { useT } from '../../../i18n/hooks';
import { LoadError } from '../../ui/LoadError';
import { CheckboxGroup, DatePicker, Field, FormActions, NumberField, Segmented, Select, TextArea, type CheckboxGroupOption } from '../../ui/forms';
import { haUnit } from '../../lib/format';
import { cropChainText } from '../my/fields/CropChain';
import { placementsByContour } from '../my/fields/fieldsModel';
import {
  buildBody,
  catalogGroups,
  fieldOptions,
  type FormState,
} from './voluntaryModel';
import '../my/my.css';
import './voluntary.css';

type Recorded = Schemas['VoluntaryRecorded'];

const m = defineMessages({
  whatCatalog: { id: 'voluntary.form.what.catalog', defaultMessage: 'каталог работ', description: 'Что не загрузилось (винительный падеж, подставляется в «Не удалось загрузить …»): каталог видов работ' },
  whatFields: { id: 'voluntary.form.what.fields', defaultMessage: 'поля хозяйства', description: 'Что не загрузилось (винительный падеж, подставляется в «Не удалось загрузить …»): поля хозяйства' },
  work: { id: 'voluntary.form.work', defaultMessage: 'Какая работа сделана', description: 'Подпись списка видов работ в форме добровольной работы' },
  workPlaceholder: { id: 'voluntary.form.workPlaceholder', defaultMessage: '— выберите из каталога —', description: 'Пустой пункт списка видов работ в форме добровольной работы' },
  workHint: { id: 'voluntary.form.workHint', defaultMessage: 'Полива в списке нет: поливы приходят из отчёта ирригации, а не со слов хозяйства.', description: 'Подсказка под списком видов работ: почему нет полива' },
  when: { id: 'voluntary.form.when', defaultMessage: 'Когда', description: 'Заголовок группы «когда сделана работа» в форме добровольной работы' },
  oneDay: { id: 'voluntary.form.oneDay', defaultMessage: 'один день', description: 'Вариант выбора в форме добровольной работы: работа сделана в один день' },
  period: { id: 'voluntary.form.period', defaultMessage: 'период', description: 'Вариант выбора в форме добровольной работы: работа длилась несколько дней' },
  periodFrom: { id: 'voluntary.form.periodFrom', defaultMessage: 'Начало периода', description: 'Подпись поля даты: начало периода работы' },
  periodTo: { id: 'voluntary.form.periodTo', defaultMessage: 'Конец периода', description: 'Подпись поля даты: конец периода работы' },
  dateOfWork: { id: 'voluntary.form.dateOfWork', defaultMessage: 'Дата работы', description: 'Подпись поля даты: день работы' },
  where: { id: 'voluntary.form.where', defaultMessage: 'Где — поля хозяйства', description: 'Заголовок группы «на каких полях» в форме добровольной работы' },
  fieldsLoading: { id: 'voluntary.form.fieldsLoading', defaultMessage: 'Читаем поля хозяйства…', description: 'Состояние загрузки полей хозяйства в форме добровольной работы' },
  noFields: { id: 'voluntary.form.noFields', defaultMessage: 'На эту дату у хозяйства нет полей в пользовании: записать работу некуда.', description: 'Пустое состояние формы: у хозяйства на выбранную дату нет полей' },
  findContour: { id: 'voluntary.form.findContour', defaultMessage: 'Найти контур по номеру или культуре', description: 'Подсказка поля поиска над списком полей хозяйства в форме добровольной работы: искать можно по номеру контура и по культуре сезона' },
  fieldsEmptySearch: { id: 'voluntary.form.fieldsEmptySearch', defaultMessage: 'Такого поля нет.', description: 'Пусто в списке полей хозяйства в форме добровольной работы, когда поиск ничего не нашёл' },
  areaTitle: { id: 'voluntary.form.areaTitle', defaultMessage: 'Площадь по выбранным полям', description: 'Заголовок списка площадей выбранных полей в форме добровольной работы: для каждого выбранного поля — число гектаров' },
  areaAria: { id: 'voluntary.form.areaAria', defaultMessage: 'Площадь, га — {field}', description: 'aria-label поля площади выбранного поля хозяйства; {field} — название поля' },
  areaPlaceholder: { id: 'voluntary.form.areaPlaceholder', defaultMessage: 'га', description: 'Единица измерения площади, суффикс внутри поля ввода; очень коротко' },
  areaHint: { id: 'voluntary.form.areaHint', defaultMessage: 'Площадь можно оставить пустой — тогда она не названа (это не ноль). Чужой контур сервер не примет.', description: 'Подсказка под списком полей в форме добровольной работы' },
  comment: { id: 'voluntary.form.comment', defaultMessage: 'Комментарий (необязательно)', description: 'Подпись поля комментария в форме добровольной работы' },
  commentPlaceholder: { id: 'voluntary.form.commentPlaceholder', defaultMessage: 'Например: листовая подкормка карбамидом, 5 кг на гектар', description: 'Подсказка в пустом поле комментария: пример записи' },
  noteBehalf: { id: 'voluntary.form.noteBehalf', defaultMessage: 'Запись внесёт менеджер за хозяйство; в списке она будет помечена. Работа привяжется к агроциклу поля на дату сама и в процент выполнения плана не войдёт.', description: 'Пояснение над кнопками формы, когда запись вносит менеджер за хозяйство' },
  noteOwn: { id: 'voluntary.form.noteOwn', defaultMessage: 'Запись внесёте вы за своё хозяйство. Работа привяжется к агроциклу поля на дату сама и в процент выполнения плана не войдёт.', description: 'Пояснение над кнопками формы, когда запись вносит сам фермер' },
  submit: { id: 'voluntary.form.submit', defaultMessage: 'Записать работу', description: 'Кнопка отправки формы добровольной работы' },
  submitting: { id: 'voluntary.form.submitting', defaultMessage: 'Записываем…', description: 'Текст кнопки отправки формы, пока идёт отправка' },
});

/** Площадь из строки формы для поля ввода: пусто — `null` («не названа», а не ноль). */
function areaNumber(text: string | undefined): number | null {
  const raw = (text ?? '').trim().replace(',', '.');
  if (raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export interface VoluntaryFormProps {
  /** Хозяйство, за которое пишем: своё у фермера, названное у диспетчера. */
  actorId: number;
  /** Диспетчер вносит «за хозяйство» — форма говорит об этом словами. */
  onBehalf: boolean;
  /** Контур, отмеченный заранее (запись открыта с карточки контура). */
  presetContourId?: number;
  onSaved: () => void;
  onCancel: () => void;
}

/**
 * «Записать работу»: вид из каталога → свои поля → дата или период → площадь →
 * заметка. Кампании и этапа в форме нет намеренно: добровольная работа в
 * процент выполнения не входит. Право и поле проверяет сервер; форма только
 * не предлагает того, что он заведомо откажет (чужая земля, полив).
 */
export function VoluntaryForm({ actorId, onBehalf, presetContourId, onSaved, onCancel }: VoluntaryFormProps) {
  const t = useT();
  const analysis = useAnalysisContext();
  // Срез — ОДНА дата с сервера (названная контекстом либо «сегодня» сервера).
  // Дата браузера — только значение по умолчанию в полях ввода, и не позже среза.
  const slice = useSliceDate();
  const catalog = useRequest(() => api.agrocycleCatalog(analysis.params), [analysis.key]);
  const farm = useRequest(() => api.actor(actorId, analysis.params), [actorId, analysis.key]);

  const [form, setForm] = useState<FormState>({
    catalogWorkId: null,
    picked: {},
    interval: false,
    on: '',
    from: '',
    to: '',
    note: '',
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Пока человек дат не трогал, поля стоят на «сегодня по часам, но не позже среза»;
  // срез приходит с сервера позже первого показа формы.
  const [datesTouched, setDatesTouched] = useState(false);
  const defaultDay = clampToSlice(slice);
  useEffect(() => {
    if (datesTouched || !defaultDay) return;
    setForm((prev) => (prev.on === defaultDay && prev.from === defaultDay && prev.to === defaultDay ? prev : { ...prev, on: defaultDay, from: defaultDay, to: defaultDay }));
  }, [datesTouched, defaultDay]);

  const groups = useMemo(() => catalogGroups(catalog.data?.items ?? []), [catalog.data]);
  // Список «Выбор» плоский: категория — подписью под названием вместо optgroup.
  const workOptions = useMemo(
    () => groups.flatMap((group) => group.items.map((item) => ({ id: String(item.id), name: item.name, description: group.label }))),
    [groups],
  );
  const onDate = form.interval ? form.from : form.on;
  // Культуры опорного сезона по контурам — из размещений хозяйства: поля с культурой в списке первыми.
  const placed = useMemo(() => placementsByContour(farm.data?.placement_details ?? []), [farm.data]);
  const options = useMemo(
    () =>
      fieldOptions(farm.data?.land_uses ?? [], onDate, (contourId) => placed.byContour.get(String(contourId))?.find((entry) => entry.season === placed.season)?.codes ?? []),
    [farm.data, onDate, placed],
  );
  const choices = useMemo<CheckboxGroupOption[]>(
    () =>
      options.map((option) => ({
        id: option.key,
        name: option.label,
        description: option.crops.length > 0 ? cropChainText(option.crops) : undefined,
        meta: option.areaHa !== null ? haUnit(option.areaHa) : undefined,
      })),
    [options],
  );

  // Контур, открытый с его карточки, отмечается один раз — когда поля прочитаны.
  const [presetApplied, setPresetApplied] = useState(false);
  if (!presetApplied && presetContourId !== undefined && options.length > 0) {
    const preset = options.filter((option) => option.contourId === presetContourId);
    setPresetApplied(true);
    if (preset.length > 0) {
      setForm((prev) => ({
        ...prev,
        picked: Object.fromEntries(preset.map((option) => [option.key, option.areaHa === null ? '' : String(option.areaHa)])),
      }));
    }
  }

  const draft = buildBody(form, options, onBehalf ? actorId : null);
  const chosen = options.filter((option) => option.key in form.picked);
  const optionByKey = useMemo(() => new Map(options.map((option) => [option.key, option])), [options]);

  /** Отмеченные поля из списка: у новых площадь — по реестру, у прежних — как введена. */
  function pick(keys: string[]) {
    setForm((prev) => {
      const picked: Record<string, string> = {};
      for (const key of keys) {
        const known = prev.picked[key];
        const option = optionByKey.get(key);
        picked[key] = known !== undefined ? known : option?.areaHa == null ? '' : String(option.areaHa);
      }
      return { ...prev, picked };
    });
  }

  async function submit() {
    if (!draft.body || sending) return;
    setSending(true);
    setError(null);
    try {
      await request<Recorded>('/voluntary-works', analysis.params, {
        method: 'POST',
        body: JSON.stringify(draft.body),
      });
      onSaved();
    } catch (cause) {
      setError(cause instanceof ApiError || cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSending(false);
    }
  }

  if (catalog.state === 'error') {
    return <LoadError what={t(m.whatCatalog)} error={catalog.error} onRetry={catalog.reload} compact />;
  }
  if (farm.state === 'error') {
    return <LoadError what={t(m.whatFields)} error={farm.error} onRetry={farm.reload} compact />;
  }

  return (
    // Enter в поиске по списку не должен отправлять работу: отправка — только кнопкой.
    <form className="vol-form my-form" onSubmit={(event) => event.preventDefault()}>
      <Select
        label={t(m.work)}
        value={form.catalogWorkId === null ? '' : String(form.catalogWorkId)}
        onChange={(value) => setForm((prev) => ({ ...prev, catalogWorkId: value === '' ? null : Number(value) }))}
        options={workOptions}
        placeholder={t(m.workPlaceholder)}
        description={t(m.workHint)}
      />

      <Field as="fieldset" className="vol-fieldset" label={t(m.when)}>
        <Segmented
          aria-label={t(m.when)}
          value={form.interval ? 'period' : 'day'}
          onChange={(value) => setForm((prev) => ({ ...prev, interval: value === 'period' }))}
          options={[
            { id: 'day', label: t(m.oneDay) },
            { id: 'period', label: t(m.period) },
          ]}
          isFullWidth
        />
        {form.interval ? (
          <div className="vol-form__dates">
            <DatePicker
              label={t(m.periodFrom)}
              value={form.from || null}
              maxValue={slice ?? undefined}
              onChange={(value) => {
                setDatesTouched(true);
                setForm((prev) => ({ ...prev, from: value ?? '' }));
              }}
            />
            <DatePicker
              label={t(m.periodTo)}
              value={form.to || null}
              maxValue={slice ?? undefined}
              onChange={(value) => {
                setDatesTouched(true);
                setForm((prev) => ({ ...prev, to: value ?? '' }));
              }}
            />
          </div>
        ) : (
          <DatePicker
            label={t(m.dateOfWork)}
            value={form.on || null}
            maxValue={slice ?? undefined}
            onChange={(value) => {
              setDatesTouched(true);
              setForm((prev) => ({ ...prev, on: value ?? '' }));
            }}
          />
        )}
      </Field>

      {farm.state === 'loading' ? (
        <span className="my-field__hint">{t(m.fieldsLoading)}</span>
      ) : options.length === 0 ? (
        <span className="my-field__hint" role="status">
          {t(m.noFields)}
        </span>
      ) : (
        <>
          <CheckboxGroup
            label={t(m.where)}
            value={Object.keys(form.picked)}
            onChange={pick}
            options={choices}
            selectAll
            searchable
            searchPlaceholder={t(m.findContour)}
            emptyText={t(m.fieldsEmptySearch)}
            maxHeight={288}
            description={t(m.areaHint)}
          />
          {chosen.length > 0 ? (
            <Field as="fieldset" className="vol-fieldset" label={t(m.areaTitle)}>
              <ul className="vol-fields">
                {chosen.map((option) => (
                  <li key={option.key} className="vol-fields__row">
                    <span className="vol-fields__name">{option.label}</span>
                    <NumberField
                      className="vol-fields__area"
                      aria-label={t(m.areaAria, { field: option.label })}
                      value={areaNumber(form.picked[option.key])}
                      onChange={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          picked: { ...prev.picked, [option.key]: value === null ? '' : String(value) },
                        }))
                      }
                      unit={t(m.areaPlaceholder)}
                      // Площадь хранится в микрогектарах: знаков как у ввода, а не два.
                      fractionDigits={6}
                    />
                  </li>
                ))}
              </ul>
            </Field>
          ) : null}
        </>
      )}

      <TextArea
        label={t(m.comment)}
        value={form.note}
        onChange={(value) => setForm((prev) => ({ ...prev, note: value }))}
        placeholder={t(m.commentPlaceholder)}
        minRows={3}
        maxLength={240}
      />

      <p className="my-note my-note--flat">
        {onBehalf ? t(m.noteBehalf) : t(m.noteOwn)}
      </p>

      {error ? (
        <p className="verdict__error" role="alert">
          {error}
        </p>
      ) : null}

      <FormActions
        note={draft.problem}
        secondary={{ label: t(COMMON.cancel), onPress: onCancel }}
        primary={{ label: sending ? t(m.submitting) : t(m.submit), icon: Check, isDisabled: !draft.body, isPending: sending, onPress: () => void submit() }}
      />
    </form>
  );
}
